import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import type { TradePolicyCatalogField } from "../../../types/tradePolicy";
import { POLICY_TREE_INPUT_CLASS } from "./policyTreeTheme";

/** Shown with a % suffix in the rule builder. */
export function catalogFieldUsesPercentUnit(field: TradePolicyCatalogField): boolean {
  const path = field.path.toLowerCase();
  const label = `${field.label ?? ""}`.toLowerCase();
  if (path.includes("_pct") || path.includes("pct_") || path.endsWith("_percent")) return true;
  if (label.includes("%") || label.includes("percent") || label.includes("percentage")) return true;
  return false;
}

/**
 * Some catalog percent fields are stored as a fraction of 100% (0.3 → 30% band width).
 * The UI shows whole-number percent; policy payload keeps the stored fraction.
 */
export function catalogPercentHumanDisplayScale(field: TradePolicyCatalogField): number {
  const path = field.path.toLowerCase();
  if (path === "volatility.bollinger_width_pct" || path.includes("bollinger_width")) return 100;
  return 1;
}

function formatPercentNumber(n: number): string {
  const rounded = Math.round(n * 1e6) / 1e6;
  return String(rounded);
}

export function storedPercentToDisplayValue(stored: string, field: TradePolicyCatalogField): string {
  const t = stored.trim();
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return stored;
  const scale = catalogPercentHumanDisplayScale(field);
  if (scale === 1) return t;
  return formatPercentNumber(n * scale);
}

export function displayPercentToStoredValue(display: string, field: TradePolicyCatalogField): string {
  const t = display.trim();
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return display;
  const scale = catalogPercentHumanDisplayScale(field);
  if (scale === 1) return t;
  return formatPercentNumber(n / scale);
}

export function numberInputPlaceholderForField(field: TradePolicyCatalogField, op: string): string {
  const label = field.label?.trim() || "Value";
  const isPct = catalogFieldUsesPercentUnit(field);
  const human = catalogPercentHumanDisplayScale(field) > 1;
  switch (op) {
    case "range":
      return isPct ? (human ? `Min/Max % (e.g. 30–80)` : `Min/Max ${label} (%)`) : `Min/Max ${label}`;
    case "gte":
      return isPct ? (human ? `Min % (e.g. 30)` : `Min ${label} (%)`) : `Min ${label} (number)`;
    case "lte":
      return isPct ? (human ? `Max % (e.g. 80)` : `Max ${label} (%)`) : `Max ${label} (number)`;
    case "eq":
      return isPct ? (human ? `Exact % (e.g. 30)` : `Exact ${label} (%)`) : `Exact ${label} (number)`;
    case "ne":
      return isPct ? (human ? `Exclude % (e.g. 30)` : `Exclude ${label} = … (%)`) : `Exclude ${label} = …`;
    default:
      return isPct ? (human ? `Enter % (e.g. 30)` : `Enter ${label} (%)`) : `Enter number for ${label}`;
  }
}

type PolicyRuleNumericValueInputProps = {
  field: TradePolicyCatalogField;
  op: string;
  value: string;
  valueMin?: string;
  valueMax?: string;
  onValueChange: (value: string) => void;
  onValueMinChange?: (value: string) => void;
  onValueMaxChange?: (value: string) => void;
  readOnly?: boolean;
};

function NumericInputWithOptionalPercent(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  showPercent: boolean;
  className?: string;
  readOnly?: boolean;
}): React.ReactElement {
  const input = (
    <Input
      type="number"
      inputMode="decimal"
      step="any"
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      disabled={props.readOnly}
      className={cn(POLICY_TREE_INPUT_CLASS, props.className)}
    />
  );

  if (!props.showPercent) return input;

  return (
    <div className={cn("flex min-w-0 items-stretch", props.className)}>
      {input}
      <span className="inline-flex h-8 min-h-8 items-center rounded-r-md border border-l-0 border-border bg-muted px-2 font-[family-name:var(--font-primary)] text-xs font-medium text-gray-500 dark:bg-sidebar dark:text-white">
        %
      </span>
    </div>
  );
}

export function PolicyRuleNumericValueInputs(props: PolicyRuleNumericValueInputProps): React.ReactElement {
  const isPct = catalogFieldUsesPercentUnit(props.field);
  const placeholder = numberInputPlaceholderForField(props.field, props.op);
  const displayMin = storedPercentToDisplayValue(props.valueMin ?? "", props.field);
  const displayMax = storedPercentToDisplayValue(props.valueMax ?? "", props.field);
  const displayValue = storedPercentToDisplayValue(props.value, props.field);

  if (props.op === "range") {
    return (
      <div className="flex w-full items-stretch gap-2">
        <NumericInputWithOptionalPercent
          className="min-w-0 flex-1"
          showPercent={isPct}
          value={displayMin}
          onChange={(v) => props.onValueMinChange?.(displayPercentToStoredValue(v, props.field))}
          placeholder={isPct ? "Min %" : `Min ${props.field.label}`}
          readOnly={props.readOnly}
        />
        <NumericInputWithOptionalPercent
          className="min-w-0 flex-1"
          showPercent={isPct}
          value={displayMax}
          onChange={(v) => props.onValueMaxChange?.(displayPercentToStoredValue(v, props.field))}
          placeholder={isPct ? "Max %" : `Max ${props.field.label}`}
          readOnly={props.readOnly}
        />
      </div>
    );
  }

  return (
    <NumericInputWithOptionalPercent
      className="w-full"
      showPercent={isPct}
      value={displayValue}
      onChange={(v) => props.onValueChange(displayPercentToStoredValue(v, props.field))}
      placeholder={placeholder}
      readOnly={props.readOnly}
    />
  );
}
